/**
 * Yusr API JSON envelope, mirrored from DETAILED_IMPLEMENTATION_SPECS.md
 * §"API Contract Standard". The frontend and backend MUST agree on these
 * shapes — keep this file in sync with `backend/app/Http/Resources/Envelope.php`
 * and `docs/api/openapi.yaml`.
 *
 * - Success:           { data: T, meta?: M }
 * - Validation error:  { message: string, errors: Record<string, string[]> }
 * - Business error:    { message: string, code: string, details?: unknown }
 */

export interface SuccessEnvelope<T, M = Record<string, unknown> | undefined> {
  data: T;
  meta?: M;
}

export interface PaginatedMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface ValidationErrorEnvelope {
  message: string;
  errors: Record<string, string[]>;
}

export interface BusinessErrorEnvelope {
  message: string;
  code: string;
  details?: unknown;
}

export type ApiErrorEnvelope = ValidationErrorEnvelope | BusinessErrorEnvelope;

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly errors?: Record<string, string[]>;
  public readonly details?: unknown;

  constructor(args: {
    status: number;
    message: string;
    code?: string;
    errors?: Record<string, string[]>;
    details?: unknown;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.errors = args.errors;
    this.details = args.details;
  }

  isValidation(): this is ApiError & { errors: Record<string, string[]> } {
    return Boolean(this.errors);
  }

  isUnauthenticated(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNetwork(): boolean {
    return this.status === 0;
  }
}
